import { Link } from '@tanstack/react-router'
import CantileverButton, { TrashButton } from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import { EditSaveCancelButtons } from 'features/assetDatabase/components/assetOverview/EditSaveCancelButtons'
import { HAS_MISSING_COMPONENTS_FIELDS } from 'features/assetDatabase/contexts/AssetOverviewDataLayer'
import { AssetOverviewUXContext } from 'features/assetDatabase/contexts/AssetOverviewUXLayer'
import { InfoTable } from 'features/assetDatabase/utils/sharedStyles'
import { AssetOverviewEditorGate } from 'features/auth'
import { ArrowRight } from 'lucide-react'
import React, { forwardRef, RefObject, useContext, useState } from 'react'

interface ComponentCardProps {
  componentName: string
  componentID: string
  infoTable?: React.ReactNode
  featureTable?: React.ReactNode
  handleDelete?: () => void
  deleteMessage?: string
}

export const ComponentCard = forwardRef<HTMLFormElement, ComponentCardProps>(
  (
    {
      componentName,
      componentID,
      infoTable,
      featureTable,
      handleDelete,
      deleteMessage,
    },
    ref
  ) => {
    const {
      inResolveMode,
      hasMissingDerivedLayerFields,
      inEditMode,
      handleSave,
      handleEdit,
      handleCancel,
    } = useContext(AssetOverviewUXContext)
    const currentlyEditingComponent = inEditMode.includes(componentID)
    const shouldShowSaveCancelButtons =
      (currentlyEditingComponent ||
        (inResolveMode &&
          hasMissingDerivedLayerFields[HAS_MISSING_COMPONENTS_FIELDS])) ??
      false

    const [popoverOpen, setPopoverOpen] = useState(false)
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })

    const handleDeleteClick = (
      e: React.MouseEvent,
      setPopupPosition: React.Dispatch<
        React.SetStateAction<{ top: number; left: number }>
      >
    ) => {
      e.stopPropagation()
      const rect = e.currentTarget.getBoundingClientRect()
      setPopupPosition({
        top: rect.bottom + 8, // 8px below the delete icon
        left: rect.left + rect.width / 2, // Centered on delete icon
      })
      setPopoverOpen(true)
    }

    return (
      <CantileverCard key={componentID}>
        <CantileverCard.CardHeader
          leadingContent={componentName}
          trailingContent={
            <Link
              to='/details/$entryID'
              from='/assets/$siteID/$unitID'
              style={{ textDecoration: 'none' }}
              params={{
                entryID: componentID,
              }}>
              <CantileverButton primary>
                Open Component View
                <ArrowRight
                  width={'1rem'}
                  style={{ position: 'relative', top: '0.03125rem' }}
                />
              </CantileverButton>
            </Link>
          }
        />
        <CantileverCard.Section>
          <CantileverCard.SectionHeader
            leadingContent='Info'
            trailingContent={
              <AssetOverviewEditorGate>
                {popoverOpen && handleDelete && (
                  <ConfirmDeletePopup
                    entityName='Component'
                    popupPosition={popupPosition}
                    cancelDelete={() => setPopoverOpen(false)}
                    confirmDelete={() => handleDelete()}
                    deleteMessage={deleteMessage}
                  />
                )}
                <EditSaveCancelButtons
                  saveCancelButtons={shouldShowSaveCancelButtons}
                  formRef={ref as RefObject<HTMLFormElement>}
                  handleSave={handleSave}
                  handleEdit={handleEdit}
                  handleCancel={handleCancel}
                  formID={componentID}
                />
                <CantileverCard.HiddenUnlessHovering>
                  <TrashButton
                    onClick={(e) => handleDeleteClick(e, setPopupPosition)}
                  />
                </CantileverCard.HiddenUnlessHovering>
              </AssetOverviewEditorGate>
            }
          />
          <InfoTable>{infoTable}</InfoTable>
        </CantileverCard.Section>
        {featureTable}
      </CantileverCard>
    )
  }
)

ComponentCard.displayName = 'ComponentCard'
